import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SelectedMode, UrgencyLevel } from "@mestergo/shared";
import { api } from "../lib/api";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useLocale } from "../lib/i18n";
import { getTelegramWebApp } from "../lib/telegram";

type CategoryValue = "plumber" | "electrician" | "appliance_repair" | "cleaning" | "moving" | "other";

const DRAFT_KEY = "mestergo_request_draft_v2";
const CITY_OPTIONS = [
  "Chișinău",
  "Bălți",
  "Cahul",
  "Orhei",
  "Ungheni",
  "Soroca",
  "Comrat",
  "Strășeni",
] as const;

interface DraftState {
  rawText: string;
  categorySlug: CategoryValue;
  city: string;
  urgency: UrgencyLevel;
  selectedMode: SelectedMode;
  scheduledFor: string;
}

interface CreateRequestResponse {
  request: { id: string };
}

export function RequestPage() {
  const navigate = useNavigate();
  const { t } = useLocale();
  const [form, setForm] = useState<DraftState>({
    rawText: "",
    categorySlug: "electrician",
    city: "Chișinău",
    urgency: UrgencyLevel.Flexible,
    selectedMode: SelectedMode.Browse,
    scheduledFor: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const raw = window.localStorage.getItem(DRAFT_KEY);
    if (!raw) return;
    try {
      setForm((prev) => ({ ...prev, ...(JSON.parse(raw) as Partial<DraftState>) }));
    } catch {
      window.localStorage.removeItem(DRAFT_KEY);
    }
  }, []);

  useEffect(() => {
    const tg = getTelegramWebApp();
    if (!tg) return;
    tg.BackButton.show();
    const onBack = () => navigate("/");
    tg.BackButton.onClick(onBack);
    return () => {
      tg.BackButton.offClick(onBack);
      tg.BackButton.hide();
    };
  }, [navigate]);

  const categories = [
    { value: "plumber" as const, label: t.categories.plumber, icon: "🪠" },
    { value: "electrician" as const, label: t.categories.electrician, icon: "⚡" },
    { value: "appliance_repair" as const, label: t.categories.applianceRepair, icon: "🔧" },
    { value: "cleaning" as const, label: t.categories.cleaning, icon: "🧹" },
    { value: "moving" as const, label: t.categories.moving, icon: "📦" },
    { value: "other" as const, label: t.categories.other, icon: "🛠" },
  ];

  const saveDraft = () => {
    window.localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
    setSaved(true);
  };

  const toApiDateTime = (value: string) => {
    if (!value) return undefined;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return undefined;
    return date.toISOString();
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      const { data } = await api.post<CreateRequestResponse>("/requests", {
        rawText: form.rawText,
        categorySlug: form.categorySlug,
        city: form.city,
        urgency: form.urgency,
        selectedMode: form.selectedMode,
        scheduledFor: toApiDateTime(form.scheduledFor),
      });
      window.localStorage.removeItem(DRAFT_KEY);
      navigate(`/requests/${data.request.id}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="border-b border-gray-100 bg-white px-6 py-5">
        <div className="mb-4 flex items-center justify-between">
          <button type="button" onClick={() => navigate("/")} className="text-sm font-semibold text-gray-500">
            {t.back}
          </button>
          <LanguageSwitcher />
        </div>
        <div className="text-2xl font-bold text-gray-900">{t.requestPageTitle}</div>
        <div className="mt-1 text-sm text-gray-500">{t.requestPageSubtitle}</div>
      </div>

      <form className="space-y-4 p-5" onSubmit={handleSubmit}>
        {/* Step 1 — Description */}
        <section className="rounded-2xl bg-white p-5 shadow-sm">
          <div className="mb-3 text-xs font-bold uppercase tracking-wide text-gray-400">{t.requestStepDescription}</div>
          <textarea
            id="request-text"
            value={form.rawText}
            onChange={(event) => {
              setSaved(false);
              setForm((prev) => ({ ...prev, rawText: event.target.value }));
            }}
            rows={4}
            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-900 outline-none focus:border-orange-400 resize-none"
            placeholder={t.descriptionPlaceholder}
            required
          />
        </section>

        {/* Step 2 — Details */}
        <section className="rounded-2xl bg-white p-5 shadow-sm space-y-5">
          <div className="text-xs font-bold uppercase tracking-wide text-gray-400">{t.requestStepDetails}</div>

          {/* Category */}
          <div>
            <div className="mb-2.5 text-sm font-semibold text-gray-700">{t.category}</div>
            <div className="grid grid-cols-3 gap-2">
              {categories.map((category) => {
                const active = form.categorySlug === category.value;
                return (
                  <button
                    key={category.value}
                    type="button"
                    onClick={() => setForm((prev) => ({ ...prev, categorySlug: category.value }))}
                    className={`rounded-xl border px-2 py-3 text-center text-xs font-semibold transition-colors ${
                      active ? "border-orange-500 bg-orange-50 text-orange-600" : "border-gray-200 text-gray-700"
                    }`}
                  >
                    <div className="text-lg mb-0.5">{category.icon}</div>
                    {category.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* City */}
          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-700" htmlFor="request-city">
              {t.city}
            </label>
            <select
              id="request-city"
              value={form.city}
              onChange={(event) => setForm((prev) => ({ ...prev, city: event.target.value }))}
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none focus:border-orange-400"
            >
              <option value="">{t.chooseCity}</option>
              {CITY_OPTIONS.map((city) => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>

          {/* Urgency */}
          <div>
            <div className="mb-2.5 text-sm font-semibold text-gray-700">{t.urgency}</div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: UrgencyLevel.Flexible, label: t.urgencyFlexible, icon: "🕐" },
                { value: UrgencyLevel.Today, label: t.urgencyToday, icon: "📅" },
                { value: UrgencyLevel.Urgent, label: t.urgencyUrgent, icon: "🚨" },
              ].map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => setForm((prev) => ({ ...prev, urgency: item.value }))}
                  className={`rounded-xl border px-3 py-3 text-center text-xs font-semibold transition-colors ${
                    form.urgency === item.value ? "border-orange-500 bg-orange-50 text-orange-600" : "border-gray-200 text-gray-700"
                  }`}
                >
                  <div className="text-base mb-0.5">{item.icon}</div>
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {/* Schedule */}
          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-700" htmlFor="request-schedule">
              {t.scheduledFor}
            </label>
            <input
              id="request-schedule"
              type="datetime-local"
              value={form.scheduledFor}
              onChange={(event) => setForm((prev) => ({ ...prev, scheduledFor: event.target.value }))}
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none focus:border-orange-400"
            />
            {form.scheduledFor ? (
              <div className="mt-1.5 text-xs text-gray-400">{new Date(form.scheduledFor).toLocaleString()}</div>
            ) : null}
          </div>
        </section>

        {/* Step 3 — Mode */}
        <section className="rounded-2xl bg-white p-5 shadow-sm">
          <div className="mb-3 text-xs font-bold uppercase tracking-wide text-gray-400">{t.requestStepMode}</div>
          <div className="space-y-2.5">
            {[
              { value: SelectedMode.Browse, title: t.modeBrowse, hint: t.modeBrowseHint, icon: "👀" },
              { value: SelectedMode.Broadcast, title: t.modeBroadcast, hint: t.modeBroadcastHint, icon: "📡" },
              { value: SelectedMode.Operator, title: t.modeOperator, hint: t.modeOperatorHint, icon: "🎧" },
            ].map((mode) => {
              const active = form.selectedMode === mode.value;
              return (
                <button
                  key={mode.value}
                  type="button"
                  onClick={() => setForm((prev) => ({ ...prev, selectedMode: mode.value }))}
                  className={`w-full rounded-xl border px-4 py-3.5 text-left transition-colors ${
                    active ? "border-orange-500 bg-orange-50" : "border-gray-200 bg-white"
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <span className="text-xl">{mode.icon}</span>
                    <div>
                      <div className={`font-semibold text-sm ${active ? "text-orange-700" : "text-gray-900"}`}>{mode.title}</div>
                      <div className="mt-0.5 text-xs text-gray-500">{mode.hint}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {saved ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {t.savedDraft}
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-3 pb-6">
          <button
            type="button"
            onClick={saveDraft}
            className="rounded-xl border border-gray-200 bg-white px-4 py-4 text-sm font-semibold text-gray-700 active:bg-gray-50 transition-colors"
          >
            {t.saveDraft}
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="rounded-xl bg-orange-500 px-4 py-4 text-sm font-semibold text-white disabled:opacity-60 active:bg-orange-600 transition-colors"
          >
            {isSubmitting ? t.loading : t.submitRequest}
          </button>
        </div>
      </form>
    </div>
  );
}
